import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
/* Navbar removed */
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Users, Clock } from "lucide-react";
import { InquiryForm } from "@/components/InquiryForm";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";

// Define interface for Circuit
interface Circuit {
  id: number;
  title: string;
  location: string;
  duration: string;
  group_size: string;
  travel_dates: string;
  price: string;
  image_url: string;
  tag: string;
  description: string;
}

const Journeys = () => {
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAPI("?type=circuits")
      .then((data: any) => {
        setCircuits(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching circuits:", err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Spiritual Circuits & Sacred Destinations" 
        description="Explore our carefully crafted spiritual circuits across India. From the Ghats of Varanasi to the Himalayas, find your perfect spiritual journey."
        canonical="https://baelleaves.com/circuits"
      />


      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-sand to-background">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-primary text-sm tracking-[0.2em] uppercase font-medium">
              Spiritual Circuits
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mt-4 mb-6">
              Choose Your <span className="text-primary">Circuit</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Each circuit is carefully crafted to guide you through India's most
              transformative spiritual experiences. Small groups, local masters,
              and authentic encounters await.
            </p>
          </div>
        </div>
      </section>

      {/* Journeys Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {circuits.map((journey) => (
              <article
                key={journey.id}
                className="group bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-500 hover:-translate-y-2"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={`https://baelleaves.com/${journey.image_url}`}
                    alt={journey.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-indigo/60 to-transparent" />

                  {/* Tag */}
                  <span className="absolute top-4 left-4 bg-primary/90 text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                    {journey.tag}
                  </span>

                  {/* Price */}
                  <div className="absolute bottom-4 right-4 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-lg">
                    <span className="text-primary font-medium">{journey.price}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-serif text-xl text-foreground mb-3 group-hover:text-primary transition-colors">
                    {journey.title}
                  </h3>

                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {journey.description}
                  </p>

                  {/* Details */}
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span>{journey.location}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <span>{journey.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary" />
                        <span>{journey.group_size}</span>
                      </div>
                    </div>
                  </div>

                  {/* Date & CTA */}
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-primary" />
                      <span className="text-foreground font-medium">{journey.travel_dates}</span>
                    </div>
                    <Link to={`/stories?circuit_id=${journey.id}`}>
                      <Button variant="ghost" size="sm" className="text-primary hover:text-primary">
                        Learn More
                      </Button>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-sand">
        <div className="container mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
            Not Sure Which Circuit Is Right for You?
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Connect with our team. We'll listen to your aspirations and help you 
            choose the perfect spiritual path.
          </p>
          <InquiryForm 
            trigger={<Button variant="sacred" size="lg">Request a Tour</Button>}
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Journeys;